import { flightsRange, scale } from "./estimate";
import type { CityEstimate, UsdRange } from "./types";

/** One cost bar: a category, its range in the current view, and its share of the city's cost. */
export interface CostBar {
  key: "flights" | "hotel" | "meetingSpace" | "meals";
  label: string;
  range: UsdRange;
  /** Share of the city's priced cost, 0–1, by range midpoints. */
  share: number;
}

/** What the bars need besides the city: the view and whether flights can be priced. */
export interface CostBarsOptions {
  /** Show amounts for the whole group instead of per person. */
  totalMode: boolean;
  headcount: number;
  /** Dates and home cities are both set, so fares exist to show. */
  flightsPriceable: boolean;
}

const midpoint = (range: UsdRange) => (range.low + range.high) / 2;

/**
 * The city's priced cost categories, largest first, in the current view. Categories still
 * loading, unpriced, or zero are left out so a bar never stands for a guess.
 * @param city - The city's estimate.
 * @param options - Per person or group, the headcount, and whether flights are priceable.
 * @returns The bars, sorted by share, largest first.
 * @throws Never.
 */
export function cityCostBars(city: CityEstimate, options: CostBarsOptions): CostBar[] {
  const { totalMode, headcount, flightsPriceable } = options;
  const factor = totalMode ? headcount : 1;
  const fares = flightsPriceable && !city.flights.loading ? flightsRange(city.flights, totalMode) : null;
  const hotelPriced = !city.hotel.loading && !city.hotel.failed && city.hotel.source !== "none";

  const candidates: Array<Omit<CostBar, "share"> | null> = [
    fares && { key: "flights", label: "Flights", range: fares },
    hotelPriced ? { key: "hotel", label: "Hotel", range: scale(city.hotel.total, factor) } : null,
    { key: "meetingSpace", label: "Meeting space", range: scale(city.meetingSpace, factor) },
    { key: "meals", label: "Meals and activities", range: scale(city.meals, factor) },
  ];

  const priced = candidates.filter((bar): bar is Omit<CostBar, "share"> => bar !== null && bar.range.high > 0);
  const sum = priced.reduce((total, bar) => total + midpoint(bar.range), 0);
  return priced.map((bar) => ({ ...bar, share: midpoint(bar.range) / sum })).sort((a, b) => b.share - a.share);
}
